/**
 * # CartTieredProgressBar
 */
class CartTieredProgressBar extends HTMLElement {
  constructor() {
    super();
    this.settings = COMPONENT_CART_TIERED_PROGRESS_BAR_SETTINGS;
    this.state = {
      currentTierHandle: "",
      currentTierName: "",
      remainingAmountForGift: 0,
      completedTiers: [],
      cartTotal: 0,
      canTiersStack: false,
    };
    this.centsInADollar = 100;
  }

  connectedCallback() {
    this.container = this.querySelector(".cart-tiered-progress-bar__container");
    this.stepsContainer = this.container.querySelector(
      ".cart-tiered-progress-bar__progress-bar-container"
    );
    this.progressBar = this.container.querySelector(
      ".cart-tiered-progress-bar__progress-bar-meter-fill"
    );
    this.progressMessageContainer = this.container.querySelector(
      "#cart-tiered-progress-bar__progress-bar-meter-label"
    );

    document.addEventListener("cart:item-added", this.loadComponent.bind(this));

    document.addEventListener(
      "line-item:change:end",
      this.loadComponent.bind(this)
    );

    document.addEventListener(
      "cart-drawer:open",
      this.loadComponent.bind(this)
    );

    document.addEventListener("quick-view:open", this.loadComponent.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener(
      "line-item:change:end",
      this.loadComponent.bind(this)
    );

    document.removeEventListener(
      "cart-drawer:open",
      this.loadComponent.bind(this)
    );

    document.removeEventListener(
      "cart:item-added",
      this.loadComponent.bind(this)
    );

    document.removeEventListener(
      "quick-view:open",
      this.loadComponent.bind(this)
    );
  }

  /**
   * Loads the component and initializes its state based on the cart's total price.
   */
  async loadComponent() {
    const cartTotalPrice = await this.getUpdatedCartObject();
    // ✅ Reset completed tiers before recalculating (fixes Safari first-load glitch)
    this.state.completedTiers = [];

    this.setCartTotalPrice(cartTotalPrice);
    this.setProgressVarAriaValueNow(cartTotalPrice);

    this.state.canTiersStack = this.dataset.canTiersStack;

    if (cartTotalPrice >= this.settings.maxAmountForProgressBar) {
      this.initializeCompletedAmountForProgressBar();
    } else if (cartTotalPrice > 0) {
      this.initializeForNonZeroTotal();
    } else {
      this.initializeForZeroTotal();
    }
    this.loadElements();
  }

  /**
   * Initializes the completed amount for the progress bar when
   * the cart total exceeds the maximum amount.
   */
  initializeCompletedAmountForProgressBar() {
    const sortedTierElements = this.sortTierElements(this.settings);
    const { handle, name } = sortedTierElements.slice(-1)[0];
    this.setCurrentTierHandle(handle);
    this.setCurrentTierName(name);
    this.setRemainingAmountForGift(0);
    sortedTierElements.forEach(({ handle }) => {
      this.setItemInCompletedTiersArray(handle);
    });
  }

  /**
   * Initializes the component for a cart with a zero total.
   */

  initializeForZeroTotal() {
    const [firstTier, _] = this.sortTierElements(this.settings);
    const { handle, name, minPurchaseTrigger } = firstTier;
    const remainingAmountForGift = this.calculateRemainingAmountForGift(
      0,
      minPurchaseTrigger
    );

    this.setItemInCompletedTiersArray();
    this.setCurrentTierHandle(handle);
    this.setCurrentTierName(name);
    this.setRemainingAmountForGift(remainingAmountForGift);
  }

  /**
   * Initializes the component for a cart with a non-zero total.
   */
  initializeForNonZeroTotal() {
    const { cartTotal: cartTotalPrice } = this.state;

    // ✅ Use the same sorted order as everywhere else
    const tierElements = this.sortTierElements(this.settings);

    for (let i = 0; i < tierElements.length; i++) {
      const { handle, name, minPurchaseTrigger } = tierElements[i];
      if (cartTotalPrice >= minPurchaseTrigger) {
        this.setItemInCompletedTiersArray(handle);
      }

      if (cartTotalPrice < minPurchaseTrigger) {
        const remainingAmountForGift = this.calculateRemainingAmountForGift(
          cartTotalPrice,
          minPurchaseTrigger
        );
        this.setCartTotalPrice(cartTotalPrice);
        this.setCurrentTierHandle(handle);
        this.removeTierFromCompletedTiersArray(handle);
        this.setCurrentTierName(name);
        this.setRemainingAmountForGift(remainingAmountForGift);
        this.setProgressVarAriaValueNow(cartTotalPrice);
        i = tierElements.length;
      }
    }
  }

  /**
   * Loads the child elements of the component.
   */
  loadElements() {
    this.buildProgressMessage();
    this.buildProgressBar();
    this.checkCompletedTiers();
    this.removeLoadingState();
  }

  /**
   * Builds the progress message based on the current state.
   */

  buildProgressMessage() {
    const { currentTierName, remainingAmountForGift, currentTierHandle } =
      this.state;

    const parsedRemainingAmountForGift = (
      remainingAmountForGift / this.centsInADollar
    ).toFixed(2);

    const currentStepElement = this.querySelector(
      `.cart-tiered-progress-bar__progress-step[data-progress-tiered-handle="${currentTierHandle}"]`
    );
    if (!currentStepElement) {
      this.setProgressMessage("");
      return;
    }

    let { customUnlockedMessage, customIncentiveMessage } =
      currentStepElement.dataset;

    if (remainingAmountForGift == 0) {
      const unlocked =
        customUnlockedMessage ||
        `${currentTierName || "Free Shipping"} unlocked!`;
      this.setProgressMessage(unlocked);
      return;
    }

    if (customIncentiveMessage) {
      customIncentiveMessage = customIncentiveMessage.replace(
        "$XX",
        `$${parsedRemainingAmountForGift}`
      );
      this.setProgressMessage(customIncentiveMessage);
      return;
    }

    const message =
      parsedRemainingAmountForGift > 0
        ? `Add $${parsedRemainingAmountForGift} to unlock ${
            currentTierName || "Free Shipping"
          }`
        : "";
    this.setProgressMessage(message);
  }

  /**
   * Builds the progress bar based on the current state.
   */
  // CartTieredProgressBar.buildProgressBar
  // --------------------------------------
  // Make the visual progress align with evenly spaced tier circles.
  // - Each segment between tiers gets an equal share of the bar width.
  // - Within a segment, we interpolate based on the cart total.
  buildProgressBar() {
    const { cartTotal } = this.state;
    const sortedTiers = this.sortTierElements(this.settings);
    const triggers = sortedTiers.map((tier) => tier.minPurchaseTrigger);
    const hasFreeShipping = sortedTiers.some(
      (tier) => tier.handle === "free-shipping"
    );
    const includeZeroAnchor = hasFreeShipping && sortedTiers.length > 1;
    const adjustedTriggers = includeZeroAnchor ? [0, ...triggers] : triggers;

    if (!adjustedTriggers.length) return;

    let percentage = 0;
    const n = adjustedTriggers.length;

    // Only one tier: fallback to simple linear behavior
    if (n === 1) {
      const lastTrigger = adjustedTriggers[0] || 1;
      percentage = Math.min((cartTotal * 100) / lastTrigger, 100);
    } else if (cartTotal <= adjustedTriggers[0]) {
      // Before the first tier
      const firstTrigger = adjustedTriggers[0];

      if (firstTrigger === 0) {
        percentage = 0;
      } else {
        const localRatio = cartTotal / firstTrigger;
        const segmentSpan = 100 / (n - 1);
        percentage = localRatio * segmentSpan;
      }
    } else if (cartTotal >= adjustedTriggers[n - 1]) {
      // At or above the last tier → full bar
      percentage = 100;
    } else {
      // Between any two tiers
      const segmentSpan = 100 / (n - 1);

      for (let i = 0; i < n - 1; i += 1) {
        const t0 = adjustedTriggers[i];
        const t1 = adjustedTriggers[i + 1];

        if (cartTotal <= t1) {
          const denom = t1 - t0 || 1;
          const localRatio = (cartTotal - t0) / denom;
          const base = (i / (n - 1)) * 100;
          percentage = base + localRatio * segmentSpan;
          break;
        }
      }
    }

    // Clamp and apply
    const clamped = Math.min(Math.max(percentage, 0), 100);
    this.progressBar.style.width = `${clamped}%`;
  }

  /**
   * Checks and updates the completion status of
   * progress steps based on completed tiers.
   */
  // CartTieredProgressBar.checkCompletedTiers
  checkCompletedTiers() {
    const { completedTiers } = this.state;

    this.stepsContainer
      .querySelectorAll(".cart-tiered-progress-bar__progress-step")
      .forEach((step) => {
        if (step.hasAttribute("data-anchor-step")) return;
        // ✅ Read handle directly from the attribute (more robust in Safari)
        const handle = step.getAttribute("data-progress-tiered-handle");
        const isCompleted = completedTiers.includes(handle);

        // ✅ Always write a string attribute, not a boolean
        step.setAttribute(
          "data-tiered-completed-step",
          isCompleted ? "true" : "false"
        );
      });
  }

  /**
   * Sets the total price of the cart in the state.
   * @param {number} cartTotal - The total price of the cart.
   */

  setCartTotalPrice(cartTotal = 0) {
    this.state.cartTotal = cartTotal;
  }

  /**
   * Sets the remaining amount for gift in the state.
   * @param {number} amount - The remaining amount for gift.
   */
  setRemainingAmountForGift(amount = 0) {
    this.state.remainingAmountForGift = amount;
  }

  /**
   * Sets the name of the current tier in the state.
   * @param {string} tierName - The name of the current tier.
   */
  setCurrentTierName(tierName) {
    if (!tierName) return;
    this.state.currentTierName = tierName;
  }

  /**
   * Sets the handle of the current tier in the state.
   * @param {string} tierHandle - The handle of the current tier.
   */
  setCurrentTierHandle(tierHandle = "") {
    if (!tierHandle) return;
    this.state.currentTierHandle = tierHandle;
  }

  /**
   * Manages the addition of tier handles to the completed tiers array in the component state.
   * If 'canTiersStack' is set to 'false', this method ensures that only the highest tier reached
   * is marked as completed, except for the 'free-shipping' tier, which can stack with other tiers.
   * - If 'canTiersStack' is 'true' or the tier is 'free-shipping', the tier is added to the
   *   completed tiers array, provided it's not already present.
   *
   * @param {string} tierHandle - The handle of the tier to be added. If empty, the completed tiers
   *                              array is reset.
   */
  setItemInCompletedTiersArray(tierHandle = "") {
    if (!tierHandle) {
      this.state.completedTiers = [];
      return;
    }

    const freeShippingExists =
      this.state.completedTiers.includes("free-shipping");

    // If stacking is not allowed and the tier is not 'free-shipping', reset the array
    // but keep 'free-shipping' if it's already there
    if (
      this.state.canTiersStack === "false" &&
      tierHandle !== "free-shipping"
    ) {
      this.state.completedTiers = freeShippingExists
        ? ["free-shipping", tierHandle]
        : [tierHandle];
    } else {
      // Only add the tier if it's not already in the array
      if (!this.state.completedTiers.includes(tierHandle)) {
        this.state.completedTiers.push(tierHandle);
      }
    }
  }

  /**
   * Sets the progress message in the progress message container.
   * @param {string} message - The progress message.
   */
  setProgressMessage(message = "") {
    this.progressMessageContainer.innerHTML = message;
  }

  /**
   * Sets the ARIA value for the current total price in the progress bar.
   * @param {number} totalPrice - The total price of the cart.
   */

  setProgressVarAriaValueNow(totalPrice = 0) {
    this.progressBar.setAttribute(
      "aria-valuenow",
      totalPrice / this.centsInADollar
    );
  }

  /**
   * Calculates the remaining amount for gift based on the cart total and minimum purchase trigger.
   * @param {number} cartTotal - The total price of the cart.
   * @param {number} minPurchaseTrigger - The minimum purchase trigger for the current tier.
   * @returns {number} The remaining amount for gift.
   */
  calculateRemainingAmountForGift(cartTotal, minPurchaseTrigger) {
    return minPurchaseTrigger - cartTotal;
  }

  /**
   * Removes a tier handle from the completed tiers array in the state.
   * @param {string} handle - The handle of the tier to be removed.
   */

  removeTierFromCompletedTiersArray(handle) {
    this.state.completedTiers = this.state.completedTiers.filter(
      (el) => el !== handle
    );
  }

  /**
   * Sorts the tier elements based on the minimum purchase trigger.
   * @param {object} tierElements - The object containing tier elements.
   * @returns {Array} The sorted array of tier elements.
   */
  sortTierElements({ tierElements }) {
    if (!Array.isArray(tierElements)) return [];

    return [...tierElements].sort(
      (a, b) => a.minPurchaseTrigger - b.minPurchaseTrigger
    );
  }

  removeLoadingState() {
    this.container.classList.remove("loading");
  }

  async getUpdatedCartObject() {
    const cartRequest = await fetch(`/cart.js`);
    const cartResponse = await cartRequest.json();
    return cartResponse.items_subtotal_price;
  }
}

customElements.define("cart-tiered-progress-bar", CartTieredProgressBar);
